/**
 * แม่แบบค่าตอบแทนสำเร็จรูป (ready-to-use compensation templates).
 * ใช้ใน TemplatesTab — กดเลือกแล้วระบบสร้างค่าตั้งต้นลง DB ผ่าน hook เดิม
 * (benefits / variable-pay / bands / structure) ไม่ต้องมี migration.
 *
 * ตัวเลขทุกชุดเป็น "จุดเริ่มต้น" อิงตลาด SME ไทยแบบกว้างๆ — Owner แก้ภายหลังได้.
 * ที่มา/แนวคิด: ยกจาก eval-pilot comp-templates แล้วเติมตาราง band/structure.
 */
import type { CompStrategy, BenefitLevers } from '../../../hooks';

// ── สวัสดิการ (benefit packages) — levers แบบเดียวกับแท็บสวัสดิการ ─────────────
export interface BenefitTemplate {
  id: string;
  name: string;
  description: string;
  levers: BenefitLevers;
}

export const BENEFIT_TEMPLATES: BenefitTemplate[] = [
  {
    id: 'starter',
    name: 'ชุดเริ่มต้น',
    description: 'ประกันสังคมตามกฎหมาย + เบี้ยขยัน — เหมาะกับบริษัทที่เพิ่งเริ่ม',
    levers: { allowance: 500 },
  },
  {
    id: 'standard',
    name: 'ชุดมาตรฐาน',
    description: 'ประกันสุขภาพกลุ่ม + กองทุนสำรองเลี้ยงชีพ 3% + เบี้ยขยัน + ค่าอาหาร',
    levers: { health_insurance: 1000, allowance: 500, provident_fund_pct: 3, meal_allowance: 600 },
  },
  {
    id: 'premium',
    name: 'ชุดพรีเมียม',
    description: 'ประกันสุขภาพ+ชีวิต + กองทุนสำรองฯ 5% + ค่าครองชีพ + ค่าเดินทาง + ค่าชุด',
    levers: { health_insurance: 2000, life_insurance: 1000, allowance: 1500, provident_fund_pct: 5, travel_allowance: 1000, uniform_allowance: 3000 },
  },
  {
    id: 'sales_field',
    name: 'ชุดเซลล์/ภาคสนาม',
    description: 'เน้นค่าเดินทาง+ค่าโทรศัพท์+ค่าตำแหน่ง สำหรับงานขาย/นอกสถานที่',
    levers: { travel_allowance: 2000, phone_allowance: 500, position_allowance: 1500, allowance: 500 },
  },
  {
    id: 'reward',
    name: 'ชุดรางวัลพนักงานดีเด่น',
    description: 'เงินรางวัลพิเศษ + ปรับขึ้นเงินเดือน + โบนัสเพิ่ม สำหรับคนผลงานเด่น',
    levers: { reward_bonus: 10000, raise_pct: 5, bonus_months: 1 },
  },
];

// ── ค่าตอบแทนผันแปร (variable pay designs) ───────────────────────────────────
export interface VariablePayTemplate {
  id: string;
  name: string;
  type: 'bonus' | 'commission' | 'incentive' | 'profit_share';
  frequency: 'monthly' | 'quarterly' | 'annual' | 'on_event';
  formula: Record<string, unknown>;
  note: string;
}

export const VARIABLE_PAY_TEMPLATES: VariablePayTemplate[] = [
  { id: 'fixed_1', name: 'โบนัสคงที่ 1 เดือน', type: 'bonus', frequency: 'annual', formula: { months: 1 }, note: 'จ่ายเท่ากันทุกคน 1 เดือนของเงินเดือน' },
  { id: 'fixed_2', name: 'โบนัสคงที่ 2 เดือน', type: 'bonus', frequency: 'annual', formula: { months: 2 }, note: 'นิยมในบริษัทที่กำไรดีต่อเนื่อง' },
  { id: 'profit', name: 'โบนัสแบ่งกำไร', type: 'profit_share', frequency: 'annual', formula: { pct_of_net_profit: 10 }, note: 'ผูกกับกำไรจริง — ปีไหนกำไรน้อยจ่ายน้อย' },
  { id: 'kpi_tiered', name: 'โบนัสตามผลประเมิน (ขั้นบันได)', type: 'bonus', frequency: 'annual', formula: { tiers: { A: 2, B: 1.5, C: 1 } }, note: 'A=2 / B=1.5 / C=1 เดือน — จูงใจคนเก่ง' },
  { id: 'sales_comm', name: 'คอมมิชชั่นยอดขาย', type: 'commission', frequency: 'monthly', formula: { pct_of_sales: 3 }, note: 'เหมาะกับทีมขาย — จ่าย % ของยอดขายรายเดือน' },
];

// ── กรอบเงินเดือน/โครงสร้าง ตามระดับ (band & structure tables) ─────────────────
// แต่ละกลยุทธ์ให้ช่วง min/mid/max ต่อระดับ L1–L5 (บาท/เดือน). ระดับ > 5 ใช้ค่า L5.
export interface BandRow { level: number; min: number; mid: number; max: number }
export interface BandTable {
  id: CompStrategy;
  name: string;
  tagline: string;
  rows: BandRow[];
}

export const BAND_TABLES: BandTable[] = [
  {
    id: 'lean',
    name: 'ประหยัด (จ่ายต่ำกว่าตลาด)',
    tagline: 'คุมต้นทุน — เหมาะกับธุรกิจที่กำไรบาง',
    rows: [
      { level: 1, min: 10000, mid: 12500, max: 15000 },
      { level: 2, min: 15000, mid: 19000, max: 24000 },
      { level: 3, min: 24000, mid: 30000, max: 38000 },
      { level: 4, min: 38000, mid: 50000, max: 68000 },
      { level: 5, min: 68000, mid: 92000, max: 125000 },
    ],
  },
  {
    id: 'fair',
    name: 'เท่าตลาด',
    tagline: 'จ่ายกลางๆ ตามค่าเฉลี่ยตลาด SME ไทย',
    rows: [
      { level: 1, min: 12000, mid: 15000, max: 18000 },
      { level: 2, min: 18000, mid: 23000, max: 28000 },
      { level: 3, min: 28000, mid: 35000, max: 45000 },
      { level: 4, min: 45000, mid: 60000, max: 80000 },
      { level: 5, min: 80000, mid: 110000, max: 150000 },
    ],
  },
  {
    id: 'aggressive',
    name: 'นำตลาด (จ่ายสูง)',
    tagline: 'ดึงดูด/รักษาคนเก่ง — เหมาะกับงานที่แข่งหาคนยาก',
    rows: [
      { level: 1, min: 14000, mid: 17500, max: 21000 },
      { level: 2, min: 21000, mid: 27000, max: 33000 },
      { level: 3, min: 33000, mid: 42000, max: 53000 },
      { level: 4, min: 53000, mid: 72000, max: 95000 },
      { level: 5, min: 95000, mid: 130000, max: 175000 },
    ],
  },
];

/** หาแถวแม่แบบของระดับ (clamp 1–5 ; ระดับเกิน 5 ใช้ค่า L5). */
export function bandRowForLevel(table: BandTable, level: number): BandRow {
  const clamped = Math.max(1, Math.min(5, Math.round(level || 1)));
  const exact = table.rows.find((r) => r.level === clamped);
  if (exact) return exact;
  // fallback กันกรณีตาราง row ว่าง (ไม่ควรเกิด) — ใช้แถวสุดท้ายหรือค่าศูนย์
  return table.rows[table.rows.length - 1] ?? { level: clamped, min: 0, mid: 0, max: 0 };
}
